import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import { ErrorMessage } from "formik";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { selectInputComponentProps } from "./select-input-component.props";
import "./select-input-component.scss";
import "../../../assets/styles/variables.scss";
import EngageIcon from "../../../assets/icons/engage.svg";
import immediateIcon from "../../../assets/icons/immediate.svg";
import SubMenu from "./sub-menu";
import { leadActionScope } from "../../../features/leads/lead-filter/lead-filter.constant";
import { makeStyles } from "@mui/styles";
import CustomTooltip from "../custom-tooltip";
import CloseIcon from "@mui/icons-material/Close";
import EmailSetUpBanner from "../email-setup-required-banner/email-setup-banner";
import { useResourcePermissions } from "../../hooks/resource-permissions";
import MODULES from "../../../constants/modules";
import { useLogin } from "../../hooks/useUser";

const SelectInputComponent: React.FC<selectInputComponentProps> = ({
  fieldName,
  label,
  optional = false,
  multiple = false,
  disabled,
  value,
  onChange,
  options = [],
  isShowError = false,
  showLabelInSelect = false,
  placeholder,
  colorProp = false,
  isFilter = false,
  handleBlur,
  disableSelectedLeads,
  handleActionTrigger,
  setSelectedActions,
  selectOpen,
  handleOpen,
  notEditable,
  totalLeads = 0,
  icon,
  closeIcon,
  onClear,
  handleOption,
  tabs = false,
  isRequired = false,
  totalCampaigns = 0,
  massActionsFor = "leads",
  isEmailConfigured = false,
  onChangeWithEvent,
  customRenderValue,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showEmailBanner, setShowEmailBanner] = useState(false);
  const [anchorPosition, setAnchorPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const handleClose = () => {
    setAnchorEl(null);
    if (fieldName === "actions") {
      //To close the parent select component on close of subMenu
      handleBlur();
    }
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      },
    },
  };

  const {canExport} = useResourcePermissions(MODULES.EXPORT);
  const loginDetails = useLogin();
  const isSuperAdmin = loginDetails?.userDetails?.roles?.role === "Super Admin";

  const handleChange = (event: any) => {
    const { value } = event.target;
    const newValue = multiple
      ? Array.isArray(value)
        ? value
        : [value]
      : value;
    onChange?.(newValue);
    onChangeWithEvent?.(event, newValue);
  };

  const handleClick = (
    event: React.MouseEvent<HTMLElement>,
    option: string
  ) => {
    setAnchorEl(event?.currentTarget);
    /**
    For actions list for each menuItem we have nested menu with AllLeads,SelectedLeads
    we need to wrap MenuItem and subMenu in one parent
    if we do this MenuItem will not first child of Select so onChange fun won't call
    that's why here i am setting selected actions
    **/
    if (typeof option !== "undefined" && setSelectedActions) {
      setSelectedActions(option);
    }
    if (handleActionTrigger) handleActionTrigger(option);
  };

  const fetchTooltipText = () => {
    if (disabled) {
      if (massActionsFor === "campaigns") {
        return totalCampaigns > 0
          ? "Please select some campaigns"
          : "No campaigns available";
      }
      if (massActionsFor === "leads") {
        return totalLeads > 0
          ? "Please select some leads"
          : "No leads available";
      }
      if (massActionsFor === "customers") {
        return totalLeads > 0
          ? "Please select some customers"
          : "No customers available";
      }
    }
    return "";
  };

  //To remove  default background color for menuItem
  const useStyles = makeStyles(() => ({
    selected: {},
    rootMenuItem: {
      "&$selected": {
        backgroundColor: "transparent !important",
        "&:hover": {
          backgroundColor: "#8879501A !important",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        },
      },
      "&.Mui-focusVisible": {
        backgroundColor: "transparent !important",
      },
      "&.Mui-selected": {
        backgroundColor: "transparent !important",
      },
      "&.Mui-selected:focus": {
        backgroundColor: "transparent !important",
      },
    },
  }));

  const styledFields = [
    "allLeads",
    "geographyParam",
    "productType",
    "conditionType",
  ];
  const shouldApplyStyles = styledFields.includes(fieldName ?? "");

  const classes = useStyles();
  return (
    <Box>
      <div
        className={`form-select-input-component ${isRequired ? "required" : ""}`}
      >
        {label && !showLabelInSelect ? (
          <InputLabel
            required={optional}
            htmlFor={fieldName}
            id="demo-multiple-checkbox"
          >
            {label}
          </InputLabel>
        ) : (
          ""
        )}
        <FormControl>
          {showLabelInSelect ? (
            <InputLabel
              required={optional}
              htmlFor={fieldName}
              id="demo-multiple-checkbox"
            >
              {label}
            </InputLabel>
          ) : (
            ""
          )}
          <Select
            labelId="demo-multiple-checkbox-label"
            id="demo-multiple-checkbox"
            multiple={multiple}
            displayEmpty={true}
            value={value || ""}
            open={selectOpen}
            onOpen={() => {
              if (handleOpen) handleOpen();
            }}
            onChange={handleChange}
            onClose={() => {
              if (handleBlur) {
                handleBlur();
              }
            }}
            input={<OutlinedInput label="Tag" />}
            renderValue={(selected) => {
               if (customRenderValue) {
                 return (
                   <span className="ellipsis-text">
                     {customRenderValue(selected)}
                   </span>
                 );
               }
              if (
                !selected ||
                (Array.isArray(selected) && selected.length === 0)
              ) {
                return <span className="placeholder">{placeholder}</span>;
              }

              if (tabs && Array.isArray(selected)) {
                return (
                  <div className="selected-tabs-wrapper">
                    {selected.map((item) => (
                      <div key={item} className="selected-tab">
                        <span className="tab-label">{item}</span>
                        <span
                          className="remove-tab"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            const updated = selected.filter((i) => i !== item);
                            onChange(updated);
                          }}
                        >
                          <CloseIcon
                            fontSize="small"
                            sx={{ width: "12px", height: "12px" }}
                          />
                        </span>
                      </div>
                    ))}
                  </div>
                );
              }

              const displayText = multiple ? selected.join(", ") : selected;

              return <div className="ellipsis-text">{displayText}</div>;
            }}
            IconComponent={icon ? () => null : ExpandMoreIcon}
            MenuProps={MenuProps}
            disabled={notEditable}
          >
            {fieldName !== "actions" &&
              options.map((option: any, index: number) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={disabled}
                  className={option?.styleName ? option?.styleName : ""}
                  classes={
                    shouldApplyStyles
                      ? {
                          selected: classes.selected,
                          root: classes.rootMenuItem,
                        }
                      : {}
                  }
                  onClick={() => {
                    if (
                      isFilter &&
                      option.value === "Custom" &&
                      (!Array.isArray(value) || !value.includes("Custom")) // It was not selected before
                    ) {
                      handleBlur();
                    }
                    if (handleOption) handleOption(option);
                  }}
                >
                  {colorProp ? (
                    index < options.length - 2 ? (
                      <>
                        <div className={`color-box ${option.label}`} />
                        <ListItemText primary={option.value} />
                        {isFilter && multiple && (
                          <Checkbox
                            className="custom-checkbox"
                            checked={
                              Array.isArray(value) &&
                              value.includes(option.value)
                            }
                          />
                        )}
                      </>
                    ) : (
                      <div className="custom-container">
                        <div className="label-wrapper">
                          <img
                            src={
                              index === options.length - 2
                                ? `${immediateIcon}`
                                : `${EngageIcon}`
                            }
                            alt={option.value}
                            className="select-image"
                          />
                          <ListItemText primary={option.value} />
                        </div>
                        {isFilter && multiple && (
                          <Checkbox
                            className="custom-checkbox"
                            checked={
                              Array.isArray(value) &&
                              value.includes(option.value)
                            }
                          />
                        )}
                      </div>
                    )
                  ) : (
                    <>
                      {isFilter ? (
                        <>
                          <ListItemText primary={option.value} />
                          {multiple && (
                            <Checkbox
                              className="custom-checkbox"
                              checked={
                                Array.isArray(value) &&
                                value.includes(option.value)
                              }
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {multiple && (
                            <Checkbox
                              className="custom-checkbox"
                              checked={
                                Array.isArray(value) &&
                                value.includes(option.value)
                              }
                            />
                          )}
                          <ListItemText
                            primary={option.label ? option.label : option.value}
                          />
                        </>
                      )}
                    </>
                  )}
                </MenuItem>
              ))}
            {fieldName === "actions" &&
              options.map((option: any, index: number) => {
                const isExportAction = ["Export Leads", "Export Customers", "Export Campaigns"].includes(option.value);
                return (
                  <CustomTooltip
                    title={fetchTooltipText()}
                    offsetX={0}
                    offsetY={0}
                    key={index}
                  >
                    <span style={{ display: "block", width: "100%" }}>
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        disabled={disabled || (isExportAction && !canExport)}
                        onClick={(e) => {
                          if (disabled || (isExportAction && !canExport)) return false;
                          handleClick(e, option?.value);
                        }}
                        onMouseEnter={(e) => {
                          if (isExportAction) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setAnchorPosition({
                              top: rect.bottom + window.scrollY,
                              left: Math.min(rect.left + window.scrollX, window.innerWidth - 800 - 20),
                            });
                            setShowEmailBanner(true);
                          }
                        }}
                        
                        onMouseLeave={() => {
                          if (isExportAction) {
                            setTimeout(() => setShowEmailBanner(false), 300);
                          }
                        }}
                        style={{ pointerEvents: disabled ? "none" : "all" }}
                      >
                        {colorProp ? (
                          index < options.length - 2 ? (
                            <>
                              <div className={`color-box ${option.label}`} />
                              <ListItemText primary={option.value} />
                            </>
                          ) : (
                            <div className="custom-container">
                              <img
                                src={
                                  index === options.length - 2
                                    ? `${immediateIcon}`
                                    : `${EngageIcon}`
                                }
                                alt={option.value}
                                className="select-image"
                              />
                              <ListItemText primary={option.value} />
                            </div>
                          )
                        ) : (
                          <>
                            {multiple && (
                              <Checkbox
                                className="custom-checkbox"
                                checked={
                                  Array.isArray(value) &&
                                  value.includes(option.value)
                                }
                              />
                            )}
                            <ListItemText primary={option.value} />
                          </>
                        )}
                      </MenuItem>
                    </span>
                  </CustomTooltip>
                );
              })}
          </Select>
          {!multiple && closeIcon && value && (
            <div
              className="select-clear-icon"
              onClick={() => {
                if (onChange) onChange("");
                if (onClear) onClear();
              }}
            >
              <CloseIcon fontSize="small" />
            </div>
          )}

          {icon && (
            <div
              className="select-icon-group"
              onClick={() => {
                if (handleOpen) handleOpen();
              }} // This opens the dropdown!
            >
              <ExpandMoreIcon className="dropdown-icon" />
              <div className="custom-icon-container">
                <img src={icon} alt="custom" className="custom-icon" />
              </div>
            </div>
          )}
        </FormControl>
        {isShowError ? (
          <ErrorMessage
            name={fieldName || ""}
            component="div"
            className="error-message"
          />
        ) : (
          ""
        )}
        {showEmailBanner && anchorPosition && !isEmailConfigured && isSuperAdmin && (
            <div
              onMouseEnter={() => setShowEmailBanner(true)}
              onMouseLeave={() => setShowEmailBanner(false)}
              style={{
                position: "absolute",
                top: anchorPosition.top + 5,
                left: anchorPosition.left,
                zIndex: 1500,
              }}
            >
              <EmailSetUpBanner />
            </div>
          )}
      </div>
    </Box>
  );
};

export default SelectInputComponent;
